import { useLocation } from 'react-router-dom';

import {
  selectActiveCustomPageStreamForPath,
  useGlobalAiCommandStore,
} from './global-ai-assistant-command-store';
import { useGlobalAIAssistantOptional } from './global-ai-assistant-context';

export type CustomPageStreamStatus = {
  /** Stream is queued or in flight for the current page. */
  isActive: boolean;
  /** Whether the stream is creating a fresh page or updating an existing one. */
  mode: 'create' | 'update' | null;
  /** Most recent status text the chat is displaying (e.g. "Generating page…"). */
  message: string | null;
};

/**
 * Read whether a `startCustomPageStream` command is being processed for
 * the currently-mounted CustomDash page, plus the chat's latest status
 * message, so the page can mirror the chat's loading state on itself.
 *
 * Designed for use anywhere underneath the `GlobalAIAssistantProvider`
 * (the optional hook returns `undefined` when the provider is absent,
 * which collapses the result to inactive).
 */
export function useCustomPageStreamStatus(): CustomPageStreamStatus {
  const { pathname } = useLocation();
  const ctx = useGlobalAIAssistantOptional();
  const generationStatus = ctx?.generationStatus ?? null;
  const active = useGlobalAiCommandStore((state) =>
    selectActiveCustomPageStreamForPath(state.queue, pathname)
  );

  if (!active || active.command.kind !== 'startCustomPageStream') {
    return { isActive: false, mode: null, message: null };
  }

  const mode = active.command.mode === 'update' ? 'update' : 'create';

  return {
    isActive: true,
    mode,
    message: generationStatus,
  };
}
